import { getPortalApiBaseUrl } from '../config/portalApiBaseUrl'
import { resolveInitialDarkMode } from '../helpers/portalDarkModePreference'

const DARK_BACKGROUND_COLOR = '#0F141B'
const LIGHT_BACKGROUND_COLOR = '#FAFAFA'

function normalizeBasePath() {
  const base = process.env.BASE_URL || '/'
  return base.endsWith('/') ? base : `${base}/`
}

function normalizeApiBaseUrl() {
  const apiBase = getPortalApiBaseUrl()
  return apiBase && String(apiBase).trim()
    ? String(apiBase).trim().replace(/\/$/, '')
    : '/api'
}

function isServiceWorkerAllowed() {
  return 'serviceWorker' in navigator
    && (window.isSecureContext || window.location.hostname === 'localhost')
}

function isServiceWorkerEnabled() {
  return process.env.NODE_ENV === 'production'
    || process.env.VUE_APP_ENABLE_PWA_IN_DEV === 'true'
}

function unregisterDevelopmentServiceWorkers() {
  if (process.env.NODE_ENV === 'production' || !('serviceWorker' in navigator)) return

  navigator.serviceWorker.getRegistrations()
    .then((registrations) => Promise.all(
      registrations.map((registration) => registration.unregister()),
    ))
    .catch(() => {})
}

function setOrUpdateLink(rel, href, extra = {}) {
  let link = document.querySelector(`link[rel="${rel}"][data-portal-pwa="true"]`)
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    link.dataset.portalPwa = 'true'
    document.head.appendChild(link)
  }
  link.href = href
  Object.entries(extra).forEach(([key, value]) => {
    if (value != null) link.setAttribute(key, value)
  })
}

function setOrUpdateMeta(name, content, extra = {}) {
  if (!content) return
  let meta = Array.from(document.querySelectorAll(`meta[name="${name}"]`))
    .find((item) => Object.entries(extra).every(([key, value]) => item.getAttribute(key) === value))
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = name
    Object.entries(extra).forEach(([key, value]) => {
      if (value != null) meta.setAttribute(key, value)
    })
    document.head.appendChild(meta)
  }
  meta.content = content
}

function buildManifestUrl() {
  const apiBase = normalizeApiBaseUrl()
  const basePath = normalizeBasePath()
  const startUrl = new URL(basePath, window.location.origin).toString()
  const pwaStartUrl = new URL(startUrl)
  pwaStartUrl.searchParams.set('portalAccessMode', 'pwa_standalone')
  const scope = startUrl
  const manifestUrl = new URL(`${apiBase}/pwa/manifest.webmanifest`, window.location.origin)
  manifestUrl.searchParams.set('startUrl', pwaStartUrl.toString())
  manifestUrl.searchParams.set('scope', scope)
  manifestUrl.searchParams.set('dark', resolveInitialDarkMode() ? 'true' : 'false')
  return manifestUrl.toString()
}

function buildIconUrl() {
  const apiBase = normalizeApiBaseUrl()
  return new URL(`${apiBase}/pwa/icon.png`, window.location.origin).toString()
}

export function setupPortalPwa(hospitalConfig = {}) {
  const isDarkMode = resolveInitialDarkMode()
  const manifestUrl = buildManifestUrl()
  const iconUrl = buildIconUrl()
  const appName = hospitalConfig.nombreAplicacion || hospitalConfig.name || 'Portal del Paciente'

  setOrUpdateLink('manifest', manifestUrl, { crossorigin: 'anonymous' })
  setOrUpdateLink('apple-touch-icon', iconUrl)
  setOrUpdateMeta('theme-color', LIGHT_BACKGROUND_COLOR, { media: '(prefers-color-scheme: light)' })
  setOrUpdateMeta('theme-color', DARK_BACKGROUND_COLOR, { media: '(prefers-color-scheme: dark)' })
  setOrUpdateMeta('apple-mobile-web-app-capable', 'yes')
  setOrUpdateMeta(
    'apple-mobile-web-app-status-bar-style',
    isDarkMode ? 'black-translucent' : 'default',
  )
  setOrUpdateMeta('apple-mobile-web-app-title', appName)

  if (!isServiceWorkerEnabled()) {
    unregisterDevelopmentServiceWorkers()
    return
  }

  if (!isServiceWorkerAllowed()) return

  const registerServiceWorker = () => {
    const basePath = normalizeBasePath()
    const swUrl = `${basePath}service-worker.js`
    navigator.serviceWorker.register(swUrl, { scope: basePath }).catch((error) => {
      // eslint-disable-next-line no-console
      console.warn('[PWA] No se pudo registrar el service worker:', error)
    })
  }

  if (document.readyState === 'complete') {
    registerServiceWorker()
  } else {
    window.addEventListener('load', registerServiceWorker, { once: true })
  }
}
